import { useEffect, useRef, useState } from 'react';
import { getAllAirportData, getAllAirportDelayStats } from '@/apis/airports';
import { getCompnayTargetSettings } from '@/apis/homeAPI';
import { getActiveToken } from '@/common/helper/tokenStorage';
import useUnauthorize from '@/hooks/useUnauthorize';
import type { AirportMetaEntry } from '../../Home/map/needsAttention';

/** Delay stats are refreshed this often, as on the flat map. */
export const AIRPORT_DELAY_REFRESH_MS = 60 * 1000;
/** The company's "airport disruption" threshold when it has none saved. */
export const DEFAULT_DELAY_THRESHOLD = 30;

export interface MonitorAirportContext {
  /** Airports with a position and current delay stats, by ICAO. */
  airportMeta: Record<string, AirportMetaEntry & { name?: string }>;
  /** Delayed/cancelled % above which an airport counts as disrupted. */
  delayThreshold: number;
}

type AirportPosition = {
  lat: number;
  lng: number;
  name?: string;
  iata?: string;
  icao: string;
};

/** The airport list request(s): one per configured region, or all. */
export const airportQueries = (regions: unknown): string[] =>
  Array.isArray(regions) && regions.length > 0
    ? regions.map(
        (region) => `?regions=${encodeURIComponent(String(region))}&limit=10000`
      )
    : ['?limit=10000'];

/** Positions by ICAO from the airport list responses. */
export const airportPositions = (
  responses: unknown[]
): Record<string, AirportPosition> => {
  const positions: Record<string, AirportPosition> = {};
  responses.forEach((response) => {
    const list = (response as { data?: unknown })?.data ?? response;
    if (!Array.isArray(list)) return;
    list.forEach((airport) => {
      if (
        airport?.icao &&
        typeof airport.latitude === 'number' &&
        typeof airport.longitude === 'number'
      ) {
        positions[airport.icao] = {
          lat: airport.latitude,
          lng: airport.longitude,
          name: airport.airport || airport.iata || airport.icao,
          iata: airport.iata,
          icao: airport.icao,
        };
      }
    });
  });
  return positions;
};

/** Airports that have both a position and delay stats (as the flat map). */
export const mergeAirportMeta = (
  positions: Record<string, AirportPosition>,
  stats: Record<string, unknown>
): MonitorAirportContext['airportMeta'] => {
  const merged: MonitorAirportContext['airportMeta'] = {};
  Object.entries(stats).forEach(([icao, delayStats]) => {
    const position = positions[icao];
    if (position && delayStats && typeof delayStats === 'object')
      merged[icao] = {
        ...position,
        delayStats: delayStats as AirportMetaEntry['delayStats'],
      };
  });
  return merged;
};

/**
 * Airport delays for /monitor_v2, loaded the way the flat map loads them:
 * the company's disruption settings (threshold and regions), the airports in
 * those regions, and their delay stats every minute. Null until the first
 * stats arrive. Feeds the attention ranking ("Near ORD · 34% delays") and
 * the airports the globe draws.
 *
 * Everything belongs to the company: a new company (or signing out) clears
 * it and starts again, so one company's airports never drive another's TV.
 * The access token rotates every few minutes for the same company; that
 * reloads nothing, each request just uses the freshest token.
 */
export const useMonitorAirports = (
  token: string | null | undefined,
  companyId?: string | null
): MonitorAirportContext | null => {
  const { unauthorize } = useUnauthorize();
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const freshToken = () => getActiveToken() || tokenRef.current || '';
  // Null when signed out; otherwise the company (or any session if unknown).
  const session = token ? (companyId ?? 'session') : null;
  // Each piece carries the session it was loaded for: right after a change
  // of company, the render still holds the old company's, which must not be
  // used (the stats step would rebuild the old airports under the new one).
  const [settings, setSettings] = useState<{
    session: string;
    regions: unknown;
    threshold: number;
  } | null>(null);
  const [positions, setPositions] = useState<{
    session: string;
    value: Record<string, AirportPosition>;
  } | null>(null);
  const [context, setContext] = useState<{
    session: string;
    value: MonitorAirportContext;
  } | null>(null);

  useEffect(() => {
    // A new company starts from nothing: no stale threshold, airports or stats.
    setSettings(null);
    setPositions(null);
    setContext(null);
    if (!session) return undefined;
    let active = true;
    getCompnayTargetSettings(freshToken())
      .then((res) => {
        const body = (
          res as {
            data?: {
              data?: {
                is_success?: boolean;
                data?: {
                  airport_disruption?: number;
                  airport_disruption_regions?: unknown;
                };
              };
            };
          }
        )?.data?.data;
        const saved = body?.is_success ? body.data : undefined;
        if (!active) return;
        setSettings({
          session,
          regions: saved?.airport_disruption_regions,
          threshold:
            typeof saved?.airport_disruption === 'number'
              ? saved.airport_disruption
              : DEFAULT_DELAY_THRESHOLD,
        });
      })
      .catch((error: unknown) => {
        unauthorize(error);
        // Without settings, every region at the default threshold.
        if (active)
          setSettings({
            session,
            regions: [],
            threshold: DEFAULT_DELAY_THRESHOLD,
          });
      });
    return () => {
      active = false;
    };
  }, [session, unauthorize]);

  const ours = settings?.session === session ? settings : null;
  const regionKey = ours ? JSON.stringify(ours.regions ?? []) : null;
  useEffect(() => {
    if (!session || regionKey === null) return undefined;
    let active = true;
    Promise.all(
      airportQueries(JSON.parse(regionKey)).map((query) =>
        getAllAirportData(freshToken(), query)
      )
    )
      .then((responses) => {
        if (active && session)
          setPositions({ session, value: airportPositions(responses) });
      })
      .catch(() => {
        // No airport list: no airports on the globe or in the ranking.
      });
    return () => {
      active = false;
    };
  }, [session, regionKey]);

  const threshold = ours?.threshold;
  const places = positions?.session === session ? positions.value : null;
  useEffect(() => {
    if (!session || !places || threshold === undefined) return undefined;
    let active = true;
    const load = () =>
      getAllAirportDelayStats(freshToken())
        .then((response) => {
          const stats = ((response as { data?: unknown })?.data ??
            {}) as Record<string, unknown>;
          if (active)
            setContext({
              session,
              value: {
                airportMeta: mergeAirportMeta(places, stats),
                delayThreshold: threshold,
              },
            });
        })
        .catch(() => {
          // Keep the last stats; the next refresh tries again.
        });
    load();
    const id = window.setInterval(load, AIRPORT_DELAY_REFRESH_MS);
    return () => {
      active = false;
      window.clearInterval(id);
    };
  }, [session, places, threshold]);

  return context?.session === session ? context.value : null;
};
